import { observer } from "mobx-react";
import { EnterpriseAccountModel, productName, VerifyEmailController } from "./dialogDomain";
import {
  useDispatch,
  useLocaleFormat,
  useTranslation,
  useUserPrefs,
} from "@/stores/RootStoreContext";
import { isFailure, isOk, isPending } from "@/common/common";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { Button, LinkButton, ToggleButton } from "@/components-v2/Button";
import { Markdown } from "@/text/Markdown";
import { MTextInput } from "@/components-v2/TextInput";
import { Alert } from "@/components-v2/Alert";
import {
  copyfromAccessibleShareholdersDropdown,
  EditSingleShareholderController,
  editSingleShareholderTabs,
} from "@/shareholderRegister/shareholders/editSingleShareholderDomain";
import { Dialog } from "@/components-v2/Dialog";
import { TMessage } from "@/i18n/tt";
import { FC, Fragment, useEffect, useState } from "react";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { EditSingleShareholderForm } from "@/shareholderRegister/shareholders/EditSingleShareholderForm";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Tag } from "@/components-v2/Tag";
import {
  adminSettings,
  adminShareholdersTransaction,
  adminUsersSettings,
  openLinkInNewWindow,
} from "@/utils/navigation";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { AlertState, ConfirmState } from "@/common/alertConfirm";
import { ActorModel, ActorsSelectorModel, ActorType, actorTypeLabel } from "@/types/SelectActors";
import { compact, last, noop } from "lodash";
import { Tabs } from "@/components-v2/Tabs";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { formatOrgNum } from "@/utils/other";
import { toDate } from "@/utils/Dates";
import { RootStoreData } from "@/events/rootStoreData";
import { AcceptCompanyTermsDialogView } from "@/termOfService/TermsOfServiceDialog";
import {
  ActivateSubscriptionDialog,
  ActiveShareholderRegisterSubscriptionDialog,
  SubscriptionPaymentDialogNew,
} from "@/payment/SubscriptionPayment";
import { SendInvitationDialog } from "@/views/Users/SendInvitationDialog";
import {
  EmailEditForm,
  InviteNewAdminDialog,
  RevokeAllInAdminForm,
} from "@/portfolio/contacts/contactsComponents";
import { NewShareClassDialog } from "@/shareholderRegister/transactions/commonTransactionComponents";
import { LiveGeneralMeetingAnnouncementDialog } from "@/meetings/generalMeeting/LiveGeneralMeetingAnnouncementDialog";
import NewMeetingDialogInitializer from "@/meetings/NewMeetingDialog";
import { EditProjectNameDialog } from "@/dbot/project/EditProjectNameDialog";
import { FilePreviewDialog } from "@/components-v2/FilePreview";
import { NewPortfolioDialog } from "@/portfolio/portfolioComponents";
import {
  findShareholderPricingInterval,
  getShareIntervals,
} from "@/meetings/generalMeeting/electronicGeneralMeetingDomain";
import {
  ChangePermissionsModel,
  GranteeGroup,
  granteeIcon,
  groupMembersHeading,
  ObjectPermissionModel,
} from "@/types/Permissions";
import { Popover } from "@/components-v2/Popover";
import { MDateInput } from "@/components-v2/DateInput";
import { LocalDateFieldModel } from "@/common/commonForms";
import {
  addToSubmissionReference,
  AddToSubmissionReferenceProps,
  AltinnSignerReminderProps,
  brStatusUpdateEmailTypes,
  createSubmissionReference,
  CreateSubmissionReferenceProps,
  DecisionBRStatusUpdateProps,
  getAllSubmittedBy,
  mapBRStatusToEmailNotificationText,
  sendSignerReminder,
  sendUpdateBRStatusEmail,
  updateDecisionStatus,
} from "@/backoffice/backofficeDomain";
import { MSelect, Select } from "@/components-v2/Select";
import { dismissAlert } from "@/stores/userPrefsDomain";
import { EmptyState } from "@/components-v2/EmptyState";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import {
  InviteUserToCustomRoleDialog,
  RoleDetailsDialog,
} from "@/portfolio/contacts/customRoleComponents";
import ExportShareholders from "@/shareholderRegister/shareholders/ExportShareholders";
import ExportTransactions from "@/shareholderRegister/transactions/ExportTransactions";
import { PortfolioCompanySelector } from "@/shareholderRegister/shareholders/PortfolioCompanySelector";
import { CompanySelectorContextType } from "@/shareholderRegister/shareholders/portfolioCompanySelectorDomain";
import { ChangeTransactionDateDialog } from "@/shareholderRegister/transactions/transactionLogComponents";
import { logDebug } from "@/utils/log";
import { replayOperationTypeMessages, ReplayTransactionError } from "@/stores/TransactionStore";
import { ImportTransactionsResult } from "@/shareholderRegister/transactions/TransactionApiClient";
import { transactionLabel } from "@/types/enums/TransactionType";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { DuplicateProjectDialog } from "@/dbot/duplicateProject/duplicateProjectComponents";
import { SaveAsTemplateDialog, TemplateVisibilityDialog } from "@/templates/saveTemplateComponents";
import { MarkdownEditor } from "@/text/MarkdownEditor";
import { getDecisionName } from "@/dbot/common/commonDomain";
import { Language } from "@/i18n/languages";
import { preventDefault } from "@/utils/events";
import { toJS } from "mobx";
import { ProjectTemplateDialog } from "@/templates/templateListComponents";
import { when } from "@/utils/functions";
import { Icon } from "@/components-v2/Icon";

export const VerifyEmailDialog = observer(function VerifyEmailDialog({
  controller,
}: {
  controller: VerifyEmailController;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      onCancel={controller.closeDialog}
      title={t("verifyEmail.title")}
      onSubmit={() => controller.verifyUserEmail()}
      actions={
        isOk(controller.updatedEmailResult) ? (
          <VBox alignItems={"end"}>
            <Button variant={"primary"} label={t("close")} onClick={controller.closeDialog} />
          </VBox>
        ) : (
          <Box>
            <LinkButton
              label={t("verifyEmail.resendCode")}
              onClick={controller.sendVerificationEmail}
            />
            <Box grow justifyContent={"end"} gap={"lg"}>
              <LinkButton label={t("verifyEmail.skip")} onClick={controller.skipVerification} />
              <Button
                variant={"primary"}
                label={t("verifyEmail.verify")}
                onClick={controller.verifyUserEmail}
              />
            </Box>
          </Box>
        )
      }
    >
      <VBox gap={"md"}>
        <Markdown
          value={t("verifyEmail.codeSentTo", { emailAddress: controller.emailAddress })}
          enableLinks
        />
        <MTextInput
          label={t("verifyEmail.verificationCode")}
          model={controller.verificationCodeModel}
          autoComplete={"off"}
          disabled={isOk(controller.updatedEmailResult)}
        />
        {isOk(controller.updatedEmailResult) && (
          <Alert type={"success"} icon={"check-circle"} message={t("verifyEmail.success")} />
        )}
        {isFailure(controller.updatedEmailResult) && (
          <Alert
            type={"error"}
            message={
              <VBox alignItems={"start"}>
                {t(controller.updatedEmailResult.error)}
                <LinkButton
                  label={t("login.get-a-new-code")}
                  color={"inherit"}
                  onClick={controller.sendVerificationEmail}
                />
              </VBox>
            }
          />
        )}
      </VBox>
    </Dialog>
  );
});

export const EditShareholderDialog = observer(function EditShareholderDialog({
  controller,
  infoLabel,
}: {
  controller: EditSingleShareholderController;
  infoLabel?: TMessage;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (controller.shareholderId) {
      controller.getShareholderDetails(controller.shareholderId);
    } else {
      controller.initEmptyShareholder();
    }
  }, [controller]);

  return (
    <Dialog
      title={
        controller.shareholderId
          ? t("edit-shareholder-dialog.title", { name: controller.shareholderOrCompanyName })
          : t("edit-shareholder-dialog.title.add")
      }
      onCancel={controller.onClose}
      submitLabel={t("save")}
      onSubmit={controller.saveShareholder}
      isSubmitDisabled={
        !controller.model?.isValid ||
        (controller.isBatchUpdate && controller.companies.isNoneSelected)
      }
      isSubmitting={isPending(controller.storeShareholderResult)}
      maxWidth={"sm"}
      height={"full"}
      headerElement={
        controller.showCopyFrom && (
          <Box grow justifyContent={"end"}>
            <DropdownMenu
              {...copyfromAccessibleShareholdersDropdown(
                controller.accessibleShareholderDetails,
                controller.getShareholderDetails,
              )}
            />
          </Box>
        )
      }
      subHeaderElement={
        controller.model ? (
          <VBox borderBottom>
            <Tabs {...editSingleShareholderTabs(controller, controller.model)} />
          </VBox>
        ) : undefined
      }
    >
      {controller.isLoading ? (
        <LoadingIndicator fullscreen />
      ) : controller.model ? (
        <VBox gap={"lg"}>
          {infoLabel && <Alert message={infoLabel} type={"info"} />}
          {controller.showEditSingleShareholderForm && (
            <VBox gap={"lg"}>
              {controller.isBatchUpdate && (
                <Markdown value={t("shareholders.editSingle.multipleCompanies.tip")} enableLinks />
              )}
              <EditSingleShareholderForm
                model={controller.model}
                fetchShareholders={controller.fetchShareholders}
                isLoadingShareholders={controller.isLoadingShareholders}
                isShareholderView={controller.isShareholderView}
              />
            </VBox>
          )}
          {!controller.showEditSingleShareholderForm && controller.isBatchUpdate && (
            <VBox gap={"lg"} marginTop={"md"}>
              <PortfolioCompanySelector
                title={t("shareholders.editSingle.multipleCompanies.title")}
                companies={controller.companies}
                companySelectorContext={controller.companySelectorContext}
              />
              {controller.companySelectorContext.value?.type !==
                CompanySelectorContextType.COMPANY && (
                <Alert
                  message={{ key: "shareholders.editSingle.multipleCompanies.information" }}
                  type={"info"}
                />
              )}
            </VBox>
          )}
        </VBox>
      ) : undefined}
    </Dialog>
  );
});

const GroupMembersTag = observer(function GroupMembersTag({
  model,
  group,
}: {
  model: ChangePermissionsModel;
  group: GranteeGroup;
}) {
  return (
    <Popover
      button={
        <button type="button">
          <Tag color={"info"} label={{ type: "number", value: group.memberCount }} isButton />
        </button>
      }
      content={<GroupMembers model={model} group={group} />}
    />
  );
});

const GroupMembers = observer(function GroupMembers({
  model,
  group,
}: {
  model: ChangePermissionsModel;
  group: GranteeGroup;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <VBox gap={"xs"}>
        <span>{t(groupMembersHeading(group))}</span>
        <ul className={"ul"}>
          {model.groupMemberNames(group.grantee).map((name, i) => (
            <li key={i}>{name}</li>
          ))}
        </ul>
      </VBox>
      <span>{t("permissions.group.members-note")}</span>
    </VBox>
  );
});

export const ChangePermissionsDialog = observer(function ChangePermissionsDialog({
  model,
}: {
  model: ChangePermissionsModel;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    model.fetchPermissions();
  }, [model]);
  return (
    <Dialog
      onCancel={model.onClose}
      title={t("permissions.title", { name: model.title })}
      onSubmit={() => model.submitPermissions()}
      submitLabel={t("save")}
      isSubmitDisabled={model.isSubmitDisabled}
      isSubmitting={isPending(model.submitPermissionResult)}
      maxWidth={"sm"}
    >
      {isPending(model.objectPermissionResult) && <LoadingIndicator />}
      {isOk(model.objectPermissionResult) && (
        <VBox gap={"lg"}>
          {isFailure(model.submitPermissionResult) && (
            <Alert type={"error"} message={model.submitPermissionResult.error} />
          )}
          <VBox>
            <Box className={"font-bold"}>{t("permissions.currentPermissions")}</Box>
            <DataTable<ObjectPermissionModel>
              variant={"IN_CARD"}
              getId={(p) => p.id}
              tableData={model.currentPermissions}
              tableColumns={[
                {
                  name: "name",
                  label: "",
                  align: "left",
                  notSorted: true,
                  getValue: (p) =>
                    when(model.findGranteeGroup(p.grantee), (group) => (
                      <Box gap={"sm"} alignItems={"center"}>
                        <Icon name={group.icon} />
                        <VBox gap={0}>
                          <div>{t(group.label)}</div>
                        </VBox>
                      </Box>
                    )) ??
                    when(model.findGranteeFromPermission(p.grantee), (admin) => (
                      <Box gap={"sm"} alignItems={"center"}>
                        <Icon name={granteeIcon(p.grantee)} />
                        <span className={admin?.isRevoked ? "strikethrough" : undefined}>
                          {admin?.name}
                        </span>
                      </Box>
                    )),
                },
                {
                  name: "tag",
                  label: "",
                  align: "left",
                  notSorted: true,
                  getValue: (p) =>
                    when(model.findGranteeGroup(p.grantee), (group) => (
                      <GroupMembersTag model={model} group={group} />
                    )) ??
                    when(model.findGranteeFromPermission(p.grantee), (admin) =>
                      p.isOwner ? (
                        <Tag color="neutral" label={t("permissions.creator")} />
                      ) : admin?.isRevoked ? (
                        <Tag color="error" label={t("permissions.removed-admin")} />
                      ) : undefined,
                    ),
                },
                {
                  name: "email",
                  label: "",
                  notSorted: true,
                  getValue: (p) =>
                    model.findGranteeGroup(p.grantee) || p.isOwner
                      ? null
                      : ["mtext-input", { model: model.emailFieldFor(p), disabled: p.isOwner }],
                },
              ]}
              rowActions={(p) => [
                [
                  "icon",
                  {
                    icon: "trash",
                    color: "error",
                    disabled: p.isOwner,
                    tooltip: p.isOwner ? t("permissions.delete.disabled") : t("permissions.delete"),
                    onClick: p.removePermission,
                  },
                ],
              ]}
            />
          </VBox>
          {model.hasInvitableItems && (
            <VBox gap={"md"}>
              <strong>{t("archive.permission.invite")}</strong>
              <Box>
                <MTextInput
                  model={model.searchInvitePermission}
                  autofocus
                  icon={"magnifying-glass"}
                  placeholder={t("archive.permission.search")}
                  type={"search"}
                />
              </Box>
              <VBox gap={"lg"}>
                {[model.invitableGroups, model.invitableUsers].map(
                  (options, row) =>
                    options.length > 0 && (
                      <Box wrap key={row}>
                        {options.map((option) => (
                          <ToggleButton
                            key={option.grantee}
                            icon={"plus"}
                            variant={"small"}
                            label={t(option.label)}
                            onClick={preventDefault(() => model.addGrantee(option.grantee))}
                          />
                        ))}
                      </Box>
                    ),
                )}
              </VBox>
            </VBox>
          )}
          {model.customRolesWithArchiveDisabled.length > 0 && (
            <Alert
              icon={"info"}
              type={"info"}
              message={{
                key: "archive.permission.custom-role.archive-disabled",
                params: { roleNames: model.customRolesWithArchiveDisabled.join(", ") },
              }}
            />
          )}
          {model.isSoleUserWithRole && (
            <Alert
              message={{
                key: "permissions.inviteAdmin",
                params: {
                  companyName: model.companyName,
                  link: adminUsersSettings(model.orgNo),
                },
              }}
              type={"info"}
            />
          )}
          {!model.isSoleUserWithRole && (
            <>
              <Checkbox
                label={t("permissions.notify", { n: model.numberOfNewPermissions })}
                disabled={model.numberOfNewPermissions === 0}
                checked={model.notifyNewPermissions.value}
                onChange={(e) => model.notifyNewPermissions.setValue(e.target.checked)}
              />
              {model.notifyNewPermissions.value && <LocalizedTextEditor model={model.emailBody} />}
            </>
          )}
        </VBox>
      )}
      {isFailure(model.objectPermissionResult) && (
        <Alert type={"warning"} message={model.objectPermissionResult.error} />
      )}
    </Dialog>
  );
});

export const ConfirmDialog = observer(function ConfirmDialog({ state }: { state: ConfirmState }) {
  const { t } = useTranslation();
  return (
    <Dialog
      onCancel={state.onCancel}
      title={state.title ?? { key: "confirm" }}
      onSubmit={state.onConfirm}
      submitLabel={state.confirmLabel ?? { key: "ok" }}
      cancelLabel={state.cancelLabel}
      maxWidth={"sm"}
    >
      <Markdown value={t(state.text)} enableLinks />
    </Dialog>
  );
});

export const AlertDialog = observer(function AlertDialog({ state }: { state: AlertState }) {
  const { t } = useTranslation();
  let title: TMessage | undefined = undefined;
  if (state.title !== undefined) {
    title = state.title;
  } else if (state.image === undefined) {
    title = { key: "info" };
  }
  return (
    <Dialog cancelLabel={t("ok")} onCancel={state.onClose} title={title} maxWidth={"sm"}>
      {state.image && (
        <Box justifyContent={"center"} marginBottom={"lg"}>
          <img src={state.image.src} alt="" style={toJS(state.image?.style)} />
        </Box>
      )}
      <Markdown value={t(state.text)} enableLinks />
    </Dialog>
  );
});

export const SelectActorsDialog = observer(function SelectActorsDialog({
  model,
}: {
  model: ActorsSelectorModel;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    model.fetchActors();
  }, [model]);

  const columns: TableColumn<ActorModel>[] = compact([
    model.isMultiSelect && {
      name: "selected",
      label: "",
      labelElement: (
        <div className={"leading-normal"}>
          <Checkbox
            onChange={model.selectAllFiltered}
            checked={model.areAllFilteredSelected}
            tooltip={{ key: model.areAllFilteredSelected ? "select-none" : "select-all" }}
          />
        </div>
      ),
      align: "left",
      width: "1%",
      notSorted: true,
      getValue: (data) => <Checkbox onChange={noop} checked={data.isSelected} />,
    },
    {
      name: "name",
      label: t("name"),
      align: "left",
      getSortValue: (data) => data.actor.name,
      getValue: (data) => <div className={"whitespace-nowrap"}>{data.actor.name}</div>,
    },
    {
      name: "orgNoOrBirthDate",
      label: t("shareholders.birthDateOrOrganisationNumber"),
      align: "left",
      getSortValue: (data) =>
        data.actor.isCompany
          ? data.actor.birthDateOrOrganisationNumber
          : data.actor.birthDateOrOrganisationNumber
            ? toDate(data.actor.birthDateOrOrganisationNumber).getTime()
            : 0,
      getValue: (data) =>
        data.actor.isCompany
          ? formatOrgNum(data.actor.birthDateOrOrganisationNumber)
          : { type: "date", value: data.actor.birthDateOrOrganisationNumber },
    },
    {
      name: "company",
      label: t("company"),
      align: "left",
      notSorted: true,
      getValue: (data) =>
        data.filterAvailableActors(model.companyFilterValue).length === 1 ? (
          data.filterAvailableActors(model.companyFilterValue)[0].companyName
        ) : (
          <Select
            value={data.selectedActor}
            onSelect={data.setSelectedActor}
            items={data.filterAvailableActors(model.companyFilterValue)}
            idFn={(item) => item.reference ?? ""}
            labelFn={(item) => item.companyName ?? ""}
          />
        ),
    },
    model.selectedTab === ActorType.SHAREHOLDER && {
      name: "representativeName",
      label: t("newShareholder.contact_person"),
      align: "left",
      getSortValue: (data) => data.selectedActor.representativeName,
      getValue: (data) =>
        data.selectedActor.representativeName ? data.selectedActor.representativeName : "",
    },
  ]);

  return (
    <Dialog
      onSubmit={model.isMultiSelect ? () => model.submitActors() : undefined}
      submitLabel={t("add")}
      isSubmitting={model.isLoading}
      isSubmitDisabled={model.selectedActors.length === 0}
      onCancel={model.onClose}
      title={model.title}
      maxWidth={"lg"}
      height={"full"}
      subHeaderElement={
        model.tabs.length > 1 ? (
          <Tabs
            scrollable={false}
            tabs={model.tabs.map((tab) => ({
              id: tab,
              label: actorTypeLabel(tab),
              isActive: model.selectedTab === tab,
              onSelect: () => model.setSelectedTab(tab),
            }))}
          />
        ) : undefined
      }
    >
      {model.isLoading ? (
        <LoadingIndicator fullscreen />
      ) : model.actorsByTab.length === 0 ? (
        <VBox grow={"golden"} justifyContent={"center"}>
          <EmptyState illustration={"users-three"} title={t("actors.empty")} />
        </VBox>
      ) : (
        <VBox gap={"lg"} grow>
          <Box gap={"lg"} wrap>
            <Box>
              <MTextInput
                model={model.searchFilter}
                name={"shareholder_filter"}
                placeholder={t("shareholders.filter.search")}
                icon={"magnifying-glass"}
              />
            </Box>
            {model.isCompanyFilterVisible && model.companyFilter && (
              <Box>
                <MSelect model={model.companyFilter} />
              </Box>
            )}
            {model.isCompanyFilterVisible && model.companyFilter?.value !== null && (
              <Box alignItems={"center"}>
                <LinkButton
                  icon={"arrow-counter-clockwise"}
                  label={{ key: "showAllCompanies" }}
                  onClick={() => model.companyFilter?.setValue(null)}
                />
                <Tag label={t("new")} color={"info"} />
              </Box>
            )}
          </Box>
          {model.tabs[0] === ActorType.SHAREHOLDER &&
            (model.selectedTab === ActorType.BOARD_MEMBER ||
              model.selectedTab === ActorType.CEO) && (
              <Alert message={{ key: "actors.info.norwegian-shareholders" }} type={"info"} />
            )}
          <DataTable
            hoverable
            tableColumns={columns}
            tableData={model.actorsByTab}
            onRowClick={(row) =>
              model.isMultiSelect ? row.toggleSelectedActor() : model.selectActor(row)
            }
            rowFilter={(s) =>
              s.matches(model.searchFilter.inputValue.toLowerCase(), model.companyFilterValue)
            }
            getId={(row) => row.id}
            rowActions={(row) =>
              compact([
                row.goToUrl && [
                  "link",
                  {
                    label: { key: "details" },
                    arrow: "out",
                    onClick: [{ type: "load-url", url: row.goToUrl, newWindow: true }],
                  },
                ],
              ])
            }
          />
        </VBox>
      )}
    </Dialog>
  );
});

const DecisionApprovalDialog = observer(function DecisionApprovalDialog({
  model,
  decisionIds,
}: {
  model: LocalDateFieldModel;
  decisionIds: string[];
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <Dialog
      title={t("followup.decision.approval-date")}
      onSubmit={() =>
        dispatch(
          updateDecisionStatus(decisionIds, null, "BR_APPROVED", model.validatedValue, {
            type: "closeDialog",
          }),
        )
      }
      onCancel={[{ type: "closeDialog" }]}
      maxWidth={"sm"}
    >
      <MDateInput label={t("followup.decision.approval-date")} model={model} />
    </Dialog>
  );
});

const DecisionSignerReminderDialog = observer(function DecisionApprovalDialog({
  decisionId,
  decisions,
  signers,
  subject,
  body,
}: AltinnSignerReminderProps) {
  const { t } = useTranslation();
  const no = useTranslation(Language.NO);
  const dispatch = useDispatch();
  return (
    <Dialog
      title={t("followup.decision.altinn.sent-for-signing.reminder.dialog.title")}
      submitLabel={t("send")}
      onSubmit={() => {
        if (decisions.validate() && signers.validate() && subject.validate() && body.validate()) {
          dispatch(
            sendSignerReminder(
              decisionId,
              decisions.selected,
              signers.selected,
              subject.validatedValue,
              body.validatedValue,
              { type: "closeDialog" },
            ),
          );
        }
      }}
      onCancel={[{ type: "closeDialog" }]}
    >
      <VBox gap={"lg"}>
        {decisions.options.length > 0 && (
          <>
            <b>{t("followup.decision.altinn.dialog.decisions")}</b>
            <MCheckboxGroup model={decisions} />
          </>
        )}
        <b>{t("followup.decision.altinn.sent-for-signing.reminder.dialog.signers")}</b>
        <Box>
          {t("followup.decision.altinn.sent-for-signing.reminder.dialog.signers.description", {
            names: { type: "list", value: getAllSubmittedBy(decisions.selected) },
          })}
        </Box>
        <MCheckboxGroup model={signers} />
        <VBox marginTop={"lg"} gap={"md"}>
          <Box>{t("followup.decision.altinn.sent-for-signing.reminder.dialog.email")}</Box>
          <VBox>
            <Box>
              {t("followup.decision.altinn.sent-for-signing.reminder.dialog.email.subject")}
            </Box>
            <MTextInput model={subject} />
          </VBox>
          <VBox>
            <Box>{t("followup.decision.altinn.sent-for-signing.reminder.dialog.email.body")}</Box>
            <i>
              Viktig informasjon vedr.{" "}
              {no.t({
                type: "list",
                value: decisions.selected.map((decision) => ({
                  type: "uncapitilize",
                  value: getDecisionName(decision.decisionType),
                })),
              })}
              :
            </i>
            <MarkdownEditor value={body.inputValue} onChange={body.setAndCommitInputValue} />
          </VBox>
        </VBox>
      </VBox>
    </Dialog>
  );
});

const CreateSubmissionReferenceDialog = observer(function CreateSubmissionReferenceDialog({
  decisionId,
  decisions,
}: CreateSubmissionReferenceProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <Dialog
      title={t("followup.decision.altinn.notification-email.dialog.title")}
      submitLabel={t("followup.decision.altinn.notification-email.dialog.submit")}
      onSubmit={() => {
        if (decisions.validate()) {
          dispatch(
            createSubmissionReference(decisionId, decisions.selected, { type: "closeDialog" }),
          );
        }
      }}
      onCancel={[{ type: "closeDialog" }]}
      maxWidth={"sm"}
    >
      <VBox gap={"lg"}>
        <Box>{t("followup.decision.altinn.notification-email.dialog.description")}</Box>
        <b>{t("followup.decision.altinn.dialog.decisions")}</b>
        <MCheckboxGroup model={decisions} />
      </VBox>
    </Dialog>
  );
});

const AddToSubmissionReferenceDialog = observer(function AddToSubmissionReferenceDialog({
  decisionId,
  submissions,
}: AddToSubmissionReferenceProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const hasSubmissions = submissions.options.length > 0;
  return (
    <Dialog
      title={t("followup.decision.add-to-submission-reference")}
      submitLabel={t("followup.decision.add-to-submission-reference")}
      isSubmitDisabled={!hasSubmissions}
      onSubmit={() => {
        if (submissions.validate() && submissions.value) {
          dispatch(addToSubmissionReference(decisionId, submissions.value.reference));
        }
      }}
      onCancel={[{ type: "closeDialog" }]}
      maxWidth={"sm"}
    >
      {hasSubmissions ? (
        <VBox gap={"lg"}>
          <Box>{t("followup.decision.add-to-submission-reference.description")}</Box>
          <MRadioButtonGroup
            model={submissions}
            descriptionFn={(submission) =>
              submission
                ? { translated: submission.statuses.map((status) => t(status)).join(", ") }
                : undefined
            }
          />
        </VBox>
      ) : (
        <Box>{t("followup.decision.add-to-submission-reference.none")}</Box>
      )}
    </Dialog>
  );
});

const DecisionBRStatusUpdateDialog = observer(function DecisionBRStatusUpdateEmailDialog({
  decisionId,
  decisions,
  body,
}: DecisionBRStatusUpdateProps) {
  const { t } = useTranslation();
  const no = useTranslation(Language.NO);
  const dispatch = useDispatch();
  return (
    <Dialog
      title={t("followup.decision.notify.dialog.title", {
        name: { type: "list", value: getAllSubmittedBy(decisions.selected) },
      })}
      submitLabel={t("send")}
      onSubmit={() => {
        if (decisions.validate() && body.validate()) {
          dispatch(
            sendUpdateBRStatusEmail(
              decisions.selected,
              body.validatedValue,
              { type: "closeDialog" },
              decisionId,
            ),
          );
        }
      }}
      onCancel={[{ type: "closeDialog" }]}
    >
      <VBox gap={"lg"}>
        {decisions.options.length > 0 && (
          <>
            <b>{t("followup.decision.altinn.dialog.decisions")}</b>
            <MCheckboxGroup model={decisions} />
          </>
        )}
        <VBox marginTop={"lg"} gap={"md"}>
          <b>Email</b>
          <VBox gap={"lg"}>
            <i>
              Oppdatering til deg som admin vedr.{" "}
              {no.t({
                type: "list",
                value: decisions.selected.map((decision) => ({
                  type: "uncapitilize",
                  value: getDecisionName(decision.decisionType),
                })),
              })}
              :
            </i>
            {body.isEmpty && (
              <Box gap={"lg"}>
                {brStatusUpdateEmailTypes.map((a, index) => (
                  <ToggleButton
                    key={index}
                    icon={"plus"}
                    variant={"small"}
                    label={mapBRStatusToEmailNotificationText[a].label}
                    onClick={() =>
                      body.setAndCommitInputValue(mapBRStatusToEmailNotificationText[a].body)
                    }
                  />
                ))}
              </Box>
            )}
            <VBox>
              <MarkdownEditor value={body.inputValue} onChange={body.setAndCommitInputValue} />
              {body.error !== undefined && <Alert type={"error"} message={<>{t(body.error)}</>} />}
            </VBox>
          </VBox>
        </VBox>
      </VBox>
    </Dialog>
  );
});

//NT: this will be used for another case, so leaving it here for now.
const SubmitForSigningSuccessDialog = observer(function SigningSuccessDialog({
  onClose,
  orgNo,
}: {
  onClose: () => void;
  orgNo?: string;
}) {
  const { t } = useTranslation();
  const userPrefs = useUserPrefs();
  const dispatch = useDispatch();
  const [shouldDismissAlert, setShouldDismissAlert] = useState(true);
  return (
    <Dialog
      title={{ key: "submit-for-signing-success.message" }}
      onSubmit={() => {
        if (shouldDismissAlert) {
          dispatch(dismissAlert(userPrefs, "signingImprovements"));
        }
        onClose();
      }}
      maxWidth={"sm"}
      submitLabel={{ key: "close" }}
    >
      <VBox gap={"lg"}>
        <Markdown
          value={t("submit-for-signing-success.body", {
            companySettingsLink: adminSettings(orgNo ?? ""),
          })}
          enableLinks
        />
        <Checkbox
          checked={shouldDismissAlert}
          onChange={(e) => setShouldDismissAlert(e.target.checked)}
          label={t("submit-for-signing-success.dismiss-alert")}
        />
      </VBox>
    </Dialog>
  );
});

export const EnterpriseAccountDialog = observer(function EnterpriseAccountDialog({
  model,
}: {
  model: EnterpriseAccountModel;
}) {
  const { t, lang } = useTranslation();
  logDebug("model.account:,", model.account);
  return (
    <Dialog
      title={{
        key: model.account ? "enterprise-account.edit-details" : "enterprise-account.add-account",
      }}
      onSubmit={model.save}
      onCancel={[{ type: "closeDialog" }]}
      maxWidth={"xs"}
    >
      <VBox gap={"md"}>
        {model.isNew && <MRadioButtonGroup model={model.createMode} horizontal />}
        <MTextInput
          model={model.primaryCompanyOrgNo}
          label={{ key: "enterprise-account.primary-company" }}
        />
        {model.showImport && (
          <>
            <Box>
              <Button
                label={t("enterprise-account.import-from-tripletex")}
                variant={"secondary"}
                onClick={model.importCustomerFromTripletex}
                loading={isPending(model.findTripletexCustomerResult)}
              />
            </Box>
            {isFailure(model.findTripletexCustomerResult) && (
              <Alert message={model.findTripletexCustomerResult?.error} type={"error"} />
            )}
          </>
        )}
        {isOk(model.findTripletexCustomerResult) && (
          <Alert
            icon={"check-circle"}
            message={{
              key: "enterprise-account.import-from-tripletex.success",
              params: { company: model.name.inputValue },
            }}
            type={"success"}
          />
        )}
        {model.showForm && (
          <VBox>
            <MTextInput model={model.name} label={{ key: "enterprise-account.account-name" }} />
            <MTextInput
              model={model.internalReference}
              label={{ key: "enterprise-account.internal-reference" }}
            />
            <MTextInput
              model={model.contactName}
              label={{ key: "enterprise-account.contact-name" }}
            />
            <MTextInput
              model={model.contactEmail}
              label={{ key: "enterprise-account.contact-email" }}
            />

            <MTextInput
              model={model.keyAccountManager}
              label={{ key: "enterprise-account.key-account-manager" }}
            />
            <MTextInput
              model={model.tripletexLink}
              label={{ key: "enterprise-account.tripletex" }}
            />
            <MTextInput model={model.hubspotLink} label={{ key: "enterprise-account.hubspot" }} />

            <VBox marginTop>
              <b>{t("enterprise-account.subscription")}</b>

              <MSelect model={model.status} label={t("common.status")} />

              <MSelect
                model={model.billingFrequency}
                label={t("enterprise-account.billing-frequency")}
              />

              <Box>
                <MDateInput
                  model={model.startTime}
                  label={{ key: "enterprise-account.start-date" }}
                />
              </Box>
              <VBox marginTop>
                <Grid cols={3} gap={"lg"}>
                  <GridItem colSpan={2}>
                    <div>
                      <b>{t("enterprise-account.products")}</b>
                    </div>
                  </GridItem>
                  <b>{t("enterprise-account.limit")}</b>

                  {model.productSpecs.map((spec) => (
                    <Fragment key={spec.productId}>
                      <GridItem colSpan={2}>
                        <div>{productName(model.products, spec.productId, lang)}</div>
                        <div className={"subtle text-sm"}>
                          {t("enterprise-account.shareholder-register.description")}
                        </div>
                      </GridItem>
                      <MTextInput model={spec.limit} />
                    </Fragment>
                  ))}
                </Grid>
                <Box>
                  <DropdownMenu
                    button={{
                      type: "button",
                      variant: "secondary",
                      icon: "plus",
                      label: { key: "enterprise-account.add-product" },
                    }}
                    items={model.addProductButtons(lang)}
                  />
                </Box>
              </VBox>
            </VBox>
          </VBox>
        )}
      </VBox>
    </Dialog>
  );
});

export const ReplayErrorDialog = observer(function ReplayErrorDialog({
  orgNo,
  error,
  onClose,
}: {
  orgNo: string;
  error: ReplayTransactionError;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormat();
  return (
    <Dialog
      title={replayOperationTypeMessages[error.operation]}
      onCancel={onClose}
      cancelLabel={{ key: "close" }}
    >
      <VBox>
        <p>{t("transactions.replay-failed.text")}</p>
        <div>
          <LinkButton
            label={`${t(transactionLabel[error.transactionType])} - ${formatDate(error.transactionDate)}`}
            onClick={() =>
              openLinkInNewWindow(adminShareholdersTransaction(orgNo, error.transactionId))
            }
            icon={"arrow-square-out"}
          />
        </div>
      </VBox>
    </Dialog>
  );
});

const ImportTransactionsResultDialog = observer(function ImportTransactionsResultDialog({
  result,
  onClose,
}: {
  result: ImportTransactionsResult | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={{ key: "transactions.import" }}
      cancelLabel={{ key: "close" }}
      onCancel={onClose}
      maxWidth={"sm"}
    >
      {result === null ? (
        <Alert type={"error"} message={{ key: "transactions.import.error" }} />
      ) : result.failed.length === 0 && result.succeeded === 0 ? (
        <span>{t("transactions.import.none")}</span>
      ) : result.failed.length === 0 ? (
        <Alert
          type={"success"}
          message={t("transactions.import.success", { count: result.succeeded })}
        />
      ) : (
        <VBox gap={"sm"}>
          <Alert
            type={"error"}
            message={t("transactions.import.partial-failure", {
              count: result.failed.length,
            })}
          />
          <ul className={"list-disc pl-4"}>
            {result.failed.map((e, idx) => (
              <li key={idx}>
                {e.row >= 0
                  ? `${e.sheet} ${t("transactions.import.row")} ${e.row}: ${e.reason}`
                  : e.reason}
              </li>
            ))}
          </ul>
        </VBox>
      )}
    </Dialog>
  );
});

export const Dialogs: FC<{ store: RootStoreData }> = observer(({ store }) => {
  const dispatch = useDispatch();
  const onClose = () => dispatch([{ type: "closeDialog" }]);
  const current = last(store.dialogs);
  if (!current) {
    return null;
  }
  switch (current.type) {
    case "acceptCompanyTerms":
      return <AcceptCompanyTermsDialogView model={current.model} />;
    case "payment":
      return <SubscriptionPaymentDialogNew controller={current.model} />;
    case "sendInvitation":
      return <SendInvitationDialog store={current.model} />;
    case "inviteNewAdmin":
      return <InviteNewAdminDialog store={current.model} />;
    case "editEmailAdmin":
      return <EmailEditForm store={current.model} />;
    case "revokeAllAdminAccess":
      return <RevokeAllInAdminForm store={current.model} />;
    case "addShareType":
      return <NewShareClassDialog controller={current.model} />;
    case "activateSubscription":
      return <ActivateSubscriptionDialog controller={current.model} />;
    case "showGeneralMeetingAnnouncement":
      return (
        <LiveGeneralMeetingAnnouncementDialog
          onClose={onClose}
          shareholderPricingInterval={findShareholderPricingInterval(
            current.shareholderCount,
            getShareIntervals(),
          )}
        />
      );
    case "activeShareholderRegisterSubscription":
      return <ActiveShareholderRegisterSubscriptionDialog context={current.context} />;
    case "newMeeting":
      return (
        <NewMeetingDialogInitializer
          meetingType={current.model.meetingType}
          organisationNumber={current.model.organisationNumber}
          projectName={current.model.projectName}
          onClose={current.model.onCancel}
          projectId={current.model.projectId}
        />
      );
    case "editProjectName":
      return <EditProjectNameDialog onSave={current.save} onClose={onClose} name={current.name} />;
    case "duplicate-project":
      return <DuplicateProjectDialog {...current} />;
    case "save-as-template":
      return <SaveAsTemplateDialog state={current} />;
    case "template-visibility":
      return <TemplateVisibilityDialog state={current} />;
    case "template-project":
      return <ProjectTemplateDialog />;
    case "preview-file":
      return <FilePreviewDialog {...current} onClose={onClose} />;
    case "showCreatePortfolioDialog":
      return <NewPortfolioDialog {...current} onClose={onClose} />;
    case "verify-email":
      return <VerifyEmailDialog controller={current.controller} />;
    case "edit-shareholder":
      return (
        <EditShareholderDialog controller={current.controller} infoLabel={current.infoLabel} />
      );
    case "change-permissions":
      return <ChangePermissionsDialog model={current.model} />;
    case "confirm":
      return <ConfirmDialog state={current.state} />;
    case "alert":
      return <AlertDialog state={current.state} />;
    case "select-actors":
      return <SelectActorsDialog model={current.model} />;
    case "decision-approval-date":
      return <DecisionApprovalDialog model={current.model} decisionIds={current.decisionIds} />;
    case "create-submission-reference":
      return <CreateSubmissionReferenceDialog {...current.props} />;
    case "add-to-submission-reference":
      return <AddToSubmissionReferenceDialog {...current.props} />;
    case "decision-signer-reminder":
      return <DecisionSignerReminderDialog {...current.props} />;
    case "decision-BR-status-update":
      return <DecisionBRStatusUpdateDialog {...current.props} />;
    case "submit-for-signing-success":
      return <SubmitForSigningSuccessDialog onClose={onClose} orgNo={current.orgNo} />;
    case "open-role-details":
      return <RoleDetailsDialog model={current.model} />;
    case "invite-custom-role-user":
      return <InviteUserToCustomRoleDialog model={current.model} />;
    case "enterprise-account":
      return <EnterpriseAccountDialog model={current.model} />;
    case "export-shareholders":
      return <ExportShareholders model={current.model} />;
    case "export-transactions":
      return <ExportTransactions model={current.model} />;
    case "change-transaction-date":
      return (
        <ChangeTransactionDateDialog
          selectedTransaction={current.model.selectedTransaction}
          transactions={current.model.controller.transactionLog}
          onSubmit={current.model.controller.doChangeTransactionDate}
          submitResult={current.model.controller.changeDateResult}
          onCancel={current.model.controller.cancelChangeTransactionDate}
          infoText={current.model.infoText}
        />
      );
    case "replay-error":
      return <ReplayErrorDialog orgNo={current.orgNo} error={current.error} onClose={onClose} />;
    case "import-result":
      return <ImportTransactionsResultDialog result={current.result} onClose={onClose} />;
  }
});
